import { openSpeckleDialog } from "@/services/speckle/officeDialog";
import { useSpeckleAuthStore } from "@haskoning/vue-speckle";
import { computed, ref } from "vue";

const AUTHENTICATION_CHALLENGE_KEY = "speckleAuthenticationChallenge";

let lastAuthenticateChallenge: string | null = null;

const isLoggingIn = ref(false);
const loginError = ref<string | null>(null);
const isSsoSessionValid = ref<boolean | null>(null);
const hasSessionExpired = ref(false);

function parseAuthDialogMessage(rawMessage: string): string | null {
  let parsed: unknown;

  try {
    parsed = JSON.parse(rawMessage);
  } catch {
    return null;
  }

  if (!parsed || typeof parsed !== "object" || !("accessCode" in parsed)) {
    return null;
  }

  const { accessCode } = parsed as { accessCode: string };

  return typeof accessCode === "string" && accessCode !== "" ? accessCode : null;
}

export function useLoginSpeckle() {
  const speckleAuthStore = useSpeckleAuthStore();

  const isSpeckleReady = computed(() => speckleAuthStore.isAuthenticated && isSsoSessionValid.value === true);
  const hasSpeckleSessionExpired = computed(() => hasSessionExpired.value);

  async function refreshSsoStatus(): Promise<boolean> {
    const isValid = await speckleAuthStore.checkSsoSessionValidity(true);

    isSsoSessionValid.value = isValid;

    if (isValid) {
      hasSessionExpired.value = false;
    }

    return isValid;
  }

  async function finishSsoSession(): Promise<void> {
    await refreshSsoStatus();

    isLoggingIn.value = false;
  }

  async function checkSpeckleSsoSession(): Promise<void> {
    if (await refreshSsoStatus()) {
      isLoggingIn.value = false;
      return;
    }

    const reauthenticationUrl = speckleAuthStore.getSsoReauthenticationRedirectUrl(globalThis.location.href);

    if (!reauthenticationUrl) {
      loginError.value = "The Speckle workspace is not available, so the SSO session cannot be renewed.";
      isLoggingIn.value = false;
      return;
    }

    await new Promise<void>((resolve) => {
      openSpeckleDialog(reauthenticationUrl, {
        onMessage: () => void finishSsoSession(),
        onClose: () => {
          isLoggingIn.value = false;
          resolve();
        },
      });
    });
  }

  function goToSpeckleAuthPage(): void {
    const authUrl = speckleAuthStore.getSpeckleAuthPageLink();

    if (lastAuthenticateChallenge) {
      localStorage.setItem(AUTHENTICATION_CHALLENGE_KEY, lastAuthenticateChallenge);
    }

    lastAuthenticateChallenge = localStorage.getItem(AUTHENTICATION_CHALLENGE_KEY);

    let hasReceivedAccessCode = false;

    openSpeckleDialog(authUrl, {
      onMessage: async (message) => {
        hasReceivedAccessCode = true;

        const accessCode = parseAuthDialogMessage(message);

        if (!accessCode) {
          loginError.value = "Speckle sign-in returned an unexpected response.";
          isLoggingIn.value = false;
          return;
        }

        try {
          await speckleAuthStore.exchangeAccessCode(accessCode);
          await speckleAuthStore.checkTokenValidity();
          await checkSpeckleSsoSession();
        } catch (error: unknown) {
          loginError.value = error instanceof Error ? error.message : "Speckle sign-in failed.";
          isLoggingIn.value = false;
        }
      },
      onClose: () => {
        if (hasReceivedAccessCode) {
          return;
        }

        isLoggingIn.value = false;
      },
    });
  }

  // Flipping the state before opening the dialog is deliberate: the banner and the disabled selects
  // must be in place even when the host refuses to open a dialog outside a user gesture.
  function markSessionExpired(): void {
    isSsoSessionValid.value = false;
    hasSessionExpired.value = true;

    void loginSpeckle();
  }

  async function ensureSpeckleSession(): Promise<boolean> {
    if (!speckleAuthStore.isAuthenticated) {
      markSessionExpired();
      return false;
    }

    if (!(await speckleAuthStore.checkTokenValidity(true)) || !(await refreshSsoStatus())) {
      markSessionExpired();
      return false;
    }
    return true;
  }

  async function restoreSpeckleSession(): Promise<void> {
    if (!speckleAuthStore.isAuthenticated) {
      return;
    }

    try {
      if (await speckleAuthStore.checkTokenValidity(true)) {
        await refreshSsoStatus();
      }
    } catch (error: unknown) {
      loginError.value = error instanceof Error ? error.message : "The Speckle session could not be restored.";
    }
  }

  async function loginSpeckle(): Promise<void> {
    if (isLoggingIn.value) {
      return;
    }

    isLoggingIn.value = true;
    loginError.value = null;

    try {
      if (speckleAuthStore.isAuthenticated && (await speckleAuthStore.checkTokenValidity(true))) {
        await checkSpeckleSsoSession();
        return;
      }

      goToSpeckleAuthPage();
    } catch (error: unknown) {
      loginError.value = error instanceof Error ? error.message : "Speckle sign-in failed.";
      isLoggingIn.value = false;
    }
  }

  function logoutSpeckle(): void {
    loginError.value = null;
    isSsoSessionValid.value = null;
    hasSessionExpired.value = false;
    speckleAuthStore.disconnect();
  }

  return {
    ensureSpeckleSession,
    hasSpeckleSessionExpired,
    isLoggingIn,
    isSpeckleReady,
    loginError,
    loginSpeckle,
    logoutSpeckle,
    restoreSpeckleSession,
    checkSpeckleSsoSession,
  };
}
